import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import './index.css';
import store from './app/store';
import { Provider, useSelector } from 'react-redux';
import Guard from './Guard';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './overrides.css';

import { IdleProvider, useIdle } from './providers/IdleProvider';
import { AdsPopup } from './components/Ads/PopUpAds';
import { createTheme, ThemeProvider } from "@mui/material";
import { hydrateAccountFromPersistence, selectAccountClientSubscription } from './features/account/accountSlice';
import {
    fetchAppCustomization,
    selectPremiumUserAds,
    selectThemeSettings,
    selectAppTitle,
    selectFaviconUrl, selectFreemiumUserAds, selectIdleSeconds
} from './features/app/appSlice';
import { hidePreloader } from "./library/helper";
import SessionExpired from "./components/System/SessionExpired";
import BackendUnavailable from "./components/System/BackendUnavailable";
import SessionHeartbeat from "./components/System/SessionHeartbeat";
import { updateFavicon } from './features/app/faviconHelper';
import GlobalHelpFab from "./components/GlobalHelpFAB/GlobalHelpFAB";

export const initApp = () => async (dispatch) => {
    await dispatch(hydrateAccountFromPersistence()).unwrap();
    await dispatch(fetchAppCustomization()).unwrap();
};

const queryClient = new QueryClient();
const root = ReactDOM.createRoot(document.getElementById('root'));


function createAppTheme(themeSettings) {
    return createTheme({
        typography: {
            fontFamily: themeSettings?.font || `"poppins", sans-serif`,
        },
        palette: {
            primary: {
                main: themeSettings?.primaryColor || '#0A564B',
            },
            secondary: {
                main: themeSettings?.secondaryColor || '#E17E7F',
            },
        },
    });
}

function IdleDebug() {
    const { idle, lastActiveAt } = useIdle();

    useEffect(() => {
    }, [idle, lastActiveAt]);

    return (
        <div style={{
            position: 'fixed', bottom: 8, right: 8, background: '#0009', color: '#fff',
            padding: '6px 10px', borderRadius: 8, fontSize: 12, zIndex: 9999
        }}>
            idle: {String(idle)} • last: {new Date(lastActiveAt).toLocaleTimeString()}
        </div>
    );
}

function IdleGate() {
    const plan = useSelector(selectAccountClientSubscription);
    const premiumUserAdsSetting = useSelector(selectPremiumUserAds);
    const freemiumUserAdsSetting = useSelector(selectFreemiumUserAds);
    const isFree = plan === 1;
    const isPremium = plan === 2;

    if (!isFree && !isPremium) return null;

    if (isFree && freemiumUserAdsSetting) {
        return <AdsPopup intervalMs={10 * 1000} showWhen="idle" routeOnly={false} />;
    }

    if (premiumUserAdsSetting) {
        return <AdsPopup intervalMs={10 * 1000} showWhen="idle" routeOnly />;
    }
}

(async () => {
    let backendFailed = false;
    let sessionExpired = false;

    try {
        await store.dispatch(initApp());
    } catch (err) {
        if (err?.code === "UNAUTHORIZED") {
            sessionExpired = true;
        } else {
            backendFailed = true;
        }
    } finally {
        hidePreloader();
        const idleSeconds = selectIdleSeconds(store.getState())
        const themeSettings = selectThemeSettings(store.getState());
        const theme = createAppTheme(themeSettings);
        const appTitle = selectAppTitle(store.getState());
        const faviconUrl = selectFaviconUrl(store.getState());
        document.title = appTitle;
        updateFavicon(faviconUrl);
        root.render(
            <QueryClientProvider client={queryClient}>
                <Provider store={store}>
                    <I18nextProvider i18n={i18n}>
                        <ThemeProvider theme={theme}>
                            {sessionExpired ? (
                                <SessionExpired />
                            ) : backendFailed ? (
                                <BackendUnavailable />
                            ) : (
                                <BrowserRouter>
                                    <IdleProvider
                                        debug
                                        idleAfterMs={idleSeconds * 1000}
                                        includeMouseMove={true}
                                        includeScroll={true}
                                        includeWheel={true}
                                    >
                                        <Guard>
                                            <App />
                                            {/*<IdleDebug/>*/}
                                            <IdleGate />
                                            <SessionHeartbeat />
                                        </Guard>
                                    </IdleProvider>
                                    <GlobalHelpFab />
                                </BrowserRouter>
                            )}
                        </ThemeProvider>
                    </I18nextProvider>
                </Provider>
            </QueryClientProvider>
        );
    }
})();

reportWebVitals();
